/// Sentry initialization for the panel + zero-knowledge scrubbing.
///
/// Mirrors the posture in `apps/server/src/utils/sentry.rs`: aggressive
/// `beforeSend` and `beforeBreadcrumb` filtering so user content never
/// leaves the browser. The DSN itself is read from `VITE_SENTRY_DSN`
/// (Vite inlines env vars prefixed with `VITE_` at build time); when
/// unset, init is a no-op and the panel runs unchanged.

import * as Sentry from '@sentry/react';

/// URL path prefixes whose query strings can carry secrets.
const SENSITIVE_PATH_PREFIXES = [
  '/datasets',
  '/changesets',
  '/account/password-reset',
  '/oauth',
  '/account/connect',
];

/// Header names whose values are dropped wholesale on captured requests.
/// Compared case-insensitively.
const SENSITIVE_HEADERS = new Set([
  'authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
  'x-meridian-device',
  'x-csrf-token',
]);

export interface InitOptions {
  /// The Sentry DSN. Falls back to `import.meta.env.VITE_SENTRY_DSN`.
  dsn?: string;
  /// Release identifier; falls back to `VITE_SENTRY_RELEASE` then to
  /// `import.meta.env.MODE` (mostly useful in CI).
  release?: string;
  /// Environment label. Falls back to `VITE_SENTRY_ENVIRONMENT`, then to
  /// Vite's `MODE` ("development" / "production").
  environment?: string;
}

/// Initialize Sentry. Idempotent — safe to call multiple times; later
/// calls are no-ops. Reads config from Vite env so the panel doesn't have
/// to bake the DSN into source.
export function initSentry(opts: InitOptions = {}): void {
  const env = import.meta.env as Record<string, string | undefined>;
  const dsn = opts.dsn ?? env.VITE_SENTRY_DSN;
  if (!dsn) {
    // Silent in dev so the console isn't noisy. The server-side init logs
    // a similar message for parity.
    return;
  }

  Sentry.init({
    dsn,
    environment: opts.environment ?? env.VITE_SENTRY_ENVIRONMENT ?? env.MODE,
    release: opts.release ?? env.VITE_SENTRY_RELEASE,
    // Zero-knowledge defaults.
    sendDefaultPii: false,
    // No session replay — would screenshot the user's inbox.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    // No performance tracing for now; flip to 0.1 later when we're
    // ready to spend the quota.
    tracesSampleRate: 0,
    integrations: [],
    beforeSend: scrubEvent,
    beforeBreadcrumb: scrubBreadcrumb,
  });

  Sentry.setTag('platform', 'panel');
}

/// Apply zero-knowledge scrubbing to an outgoing Sentry event. Exposed
/// for tests; production callers use the `beforeSend` hook set in `init`.
export function scrubEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  if (event.request) {
    // Request body — always strip.
    delete event.request.data;
    // Cookies — separate field from headers.
    delete event.request.cookies;
    // Query strings on sensitive paths.
    if (event.request.url && hasSensitivePath(event.request.url)) {
      delete event.request.query_string;
    }
    // Sensitive headers (case-insensitive comparison).
    if (event.request.headers) {
      event.request.headers = filterHeaders(event.request.headers);
    }
  }

  // User context: keep `id` only.
  if (event.user) {
    delete event.user.email;
    delete event.user.username;
    delete event.user.ip_address;
  }

  // server_name leaks deployment topology.
  delete event.server_name;

  return event;
}

/// Apply scrubbing to a breadcrumb before it's attached to an event.
/// Sentry's default fetch/xhr breadcrumbs include the URL, which can
/// carry the same secrets we strip from request data.
export function scrubBreadcrumb(
  breadcrumb: Sentry.Breadcrumb,
): Sentry.Breadcrumb | null {
  // Drop console-log breadcrumbs entirely — they routinely carry user
  // content (search queries, message previews) that we don't want
  // crossing the wire.
  if (breadcrumb.category === 'console') {
    return null;
  }

  // HTTP breadcrumbs: strip query strings on sensitive paths.
  if (
    (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') &&
    typeof breadcrumb.data?.url === 'string' &&
    hasSensitivePath(breadcrumb.data.url)
  ) {
    breadcrumb.data = { ...breadcrumb.data, url: stripQuery(breadcrumb.data.url) };
  }

  return breadcrumb;
}

function hasSensitivePath(url: string): boolean {
  try {
    const parsed = new URL(url, 'http://placeholder.invalid');
    return SENSITIVE_PATH_PREFIXES.some((prefix) =>
      parsed.pathname.startsWith(prefix),
    );
  } catch {
    return false;
  }
}

function stripQuery(url: string): string {
  try {
    const parsed = new URL(url, 'http://placeholder.invalid');
    parsed.search = '';
    // If we used the placeholder base, return just the path.
    if (parsed.origin === 'http://placeholder.invalid') {
      return parsed.pathname;
    }
    return parsed.toString();
  } catch {
    return url;
  }
}

function filterHeaders(
  headers: Record<string, string>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [name, value] of Object.entries(headers)) {
    if (!SENSITIVE_HEADERS.has(name.toLowerCase())) {
      out[name] = value;
    }
  }
  return out;
}
